type EventCallback<T = unknown> = (data: T) => void;

interface EventSubscription {
  unsubscribe: () => void;
}

/**
 * Simple pub/sub event system for decoupled communication
 */
export class EventBus {
  private listeners: Map<string, Set<EventCallback>> = new Map();

  /**
   * Subscribe to an event
   */
  on<T>(event: string, callback: EventCallback<T>): EventSubscription {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as EventCallback);

    return {
      unsubscribe: () => this.off(event, callback),
    };
  }

  /**
   * Subscribe to an event once
   */
  once<T>(event: string, callback: EventCallback<T>): EventSubscription {
    const wrapper: EventCallback<T> = (data) => {
      this.off(event, wrapper);
      callback(data);
    };
    return this.on(event, wrapper);
  }

  /**
   * Unsubscribe from an event
   */
  off<T>(event: string, callback: EventCallback<T>): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback as EventCallback);
      if (callbacks.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Emit an event to all subscribers
   */
  emit<T>(event: string, data?: T): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => callback(data));
    }
  }

  /**
   * Remove all listeners for an event, or all events if no event specified
   */
  clear(event?: string): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

// Global event bus instance
export const globalEventBus = new EventBus();

// Event types for type safety
export const GameEvents = {
  HEX_HOVERED: 'hex:hovered',
  HEX_CLICKED: 'hex:clicked',
  HEX_UNHOVERED: 'hex:unhovered',
  ENTITY_SPAWNED: 'entity:spawned',
  ENTITY_DESTROYED: 'entity:destroyed',
  GAME_STATE_CHANGED: 'game:stateChanged',
  WINDOW_RESIZED: 'window:resized',
} as const;
